import {Subject} from 'rxjs';
import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {AbstractControl, ControlValueAccessor, UntypedFormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn} from '@angular/forms';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-ace-chips',
  templateUrl: './ace-chips.component.html',
  styleUrls: ['./ace-chips.component.scss'],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AceChipsComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => AceChipsComponent), multi: true}
  ],
  standalone: false
})
export class AceChipsComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy, Validator {

  public static readonly INPUT_ERROR_KEY_PREFIX = 'input_error_';

  private static readonly MULTI_VALUE_SPLIT_CHAR = ',';

  @Input() label: string;
  @Input() isReadonly = false;
  @Input() placeholder = '';
  @Input() addDuplicates = false;
  @Input() sorted = true;
  @Input() hideLabel = false;
  @Input() inputValidators: ValidatorFn[];

  @Output() blurEvent = new EventEmitter<void>();

  items: any[] = [];
  formControl: UntypedFormControl = new UntypedFormControl();

  private readonly ngUnsubscribe = new Subject<void>();

  public static splitValue(value: string): string[] | null {
    if (value) {
      return value.trim().split(AceChipsComponent.MULTI_VALUE_SPLIT_CHAR);
    } else {
      return null;
    }
  }

  private onChange: (value: any) => void = (_value: any) => {};
  private onTouched: () => void = () => {};
  private validatorChangeFn: () => void = () => {};

  constructor() {}

  ngOnInit(): void {
    this.formControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(_value => {
      if (this.inputValidators?.length) {
        this.validatorChangeFn();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.inputValidators) {
      this.formControl.setValidators(this.inputValidators?.length ? this.inputValidators : null);
      this.formControl.updateValueAndValidity();
      this.validatorChangeFn();
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  registerOnValidatorChange(fn: () => void): void {
    this.validatorChangeFn = fn;
  }

  validate(c: AbstractControl): ValidationErrors | null {
    const errors = this.formControl.errors;
    if (errors) {
      const errorsWithPrefix = {};
      Object.entries(errors).forEach(([key, value]) => errorsWithPrefix[AceChipsComponent.INPUT_ERROR_KEY_PREFIX + key] = value);
      return errorsWithPrefix;
    } else {
      return null;
    }
  }

  writeValue(items: any[]): void {
    this.items = items?.length ? items : [];
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /**
   * Can be called externally to force the control to add the text that is being currently typed in to the list
   * of actual values.
   */
  public add(): void {
    if (this.formControl.valid) {
      this.addItems(this.formControl.value);
    }
  }

  remove(index: number): void {
    this.items.splice(index, 1);
    this.fireOnChange();
  }

  onFocus() {
    if (this.inputValidators?.length) {
      this.onTouched();
    }
  }

  onBlur(): void {
    this.onTouched();
    this.blurEvent.next();
  }

  private addItems(itemsString: string): void {
    const value = AceChipsComponent.splitValue(itemsString);
    if (value?.length) {
      value.forEach(listItem => {
        const item = listItem?.trim();
        if (item?.length && (this.addDuplicates || !this.items.includes(item))) {
          this.items.push(item);
        }
      });

      if (this.sorted) {
        this.items.sort();
      }
      this.formControl.reset();
      this.fireOnChange();
    }
  }

  private fireOnChange(): void {
    this.onTouched();
    this.onChange(this.items);
  }
}
